import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Check, Mail, MessageSquare, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  system: AlertCircle,
  email: Mail,
  sms: MessageSquare,
};

const TYPE_COLORS = {
  system: '#3b82f6',
  email: '#f59e0b',
  sms: '#8b5cf6',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationsBell({ tenantId, accentColor = '#FACC15', onNavigate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchUnread = async () => {
    if (!tenantId) return;
    try {
      const res = await axios.get(`${API}/daycare/${tenantId}/notifications/unread-count`);
      setUnread(res.data.count || 0);
    } catch {
      /* ignore */
    }
  };

  const fetchList = async () => {
    if (!tenantId) return;
    try {
      const res = await axios.get(`${API}/daycare/${tenantId}/notifications`);
      setItems((res.data || []).slice(0, 12));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (open) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (notifId) => {
    try {
      await axios.put(`${API}/daycare/${tenantId}/notifications/${notifId}/read`);
      setItems(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      fetchUnread();
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/daycare/${tenantId}/notifications/read-all`);
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  const handleItemClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    setOpen(false);
    if (onNavigate) {
      const trigger = (notif.trigger || '').toLowerCase();
      if (trigger.includes('payment') || trigger.includes('tuition')) onNavigate('payments');
      else if (trigger.includes('report')) onNavigate('reports');
      else if (trigger.includes('attendance')) onNavigate('attendance');
      else if (trigger.includes('invite')) onNavigate('parents');
      else onNavigate('notifications');
    }
  };

  return (
    <div className="notif-bell-wrap" ref={ref} style={{ position: 'relative' }}>
      <button
        className="icon-btn"
        onClick={() => setOpen(o => !o)}
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && <span className="badge" data-testid="notification-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div className="notif-dropdown" data-testid="notification-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 360,
          maxHeight: 480, overflow: 'hidden', background: '#fff',
          border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,23,42,.12)',
          zIndex: 1000, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc'
          }}>
            <strong style={{ fontSize: 14, color: '#0f172a' }}>Notifications</strong>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                style={{ background: 'transparent', border: 0, color: '#2563eb', fontSize: 12, cursor: 'pointer' }}
                data-testid="notification-mark-all-read"
              >
                <Check size={12} style={{ verticalAlign: 'middle' }} /> Mark all read
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {items.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }} data-testid="notification-empty">
                No notifications yet
              </div>
            ) : (
              items.map(notif => {
                const Icon = TYPE_ICONS[notif.type] || AlertCircle;
                const color = TYPE_COLORS[notif.type] || '#64748b';
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleItemClick(notif)}
                    data-testid={`notification-item-${notif.id}`}
                    style={{
                      width: '100%', textAlign: 'left', display: 'flex', gap: 10,
                      padding: '10px 14px', border: 0, borderBottom: '1px solid #f1f5f9',
                      background: notif.read ? '#fff' : '#f0f9ff', cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, background: `${color}1a`,
                      display: 'grid', placeItems: 'center', color, flexShrink: 0,
                    }}>
                      <Icon size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: notif.read ? 400 : 600, lineHeight: 1.4 }}>
                        {notif.message || '(no message)'}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {notif.recipient ? `To: ${notif.recipient} • ` : ''}{timeAgo(notif.created_at)}
                      </div>
                    </div>
                    {!notif.read && <span style={{ width: 8, height: 8, borderRadius: '50%', background: accentColor, marginTop: 12 }} />}
                  </button>
                );
              })
            )}
          </div>
          <button
            onClick={() => { setOpen(false); if (onNavigate) onNavigate('notifications'); }}
            style={{
              padding: '10px 14px', background: '#f8fafc', borderTop: '1px solid #f1f5f9',
              border: 0, cursor: 'pointer', fontSize: 13, color: '#2563eb', fontWeight: 500,
            }}
            data-testid="notification-view-all"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
